import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../../atoms/Button'
import { Icon } from '../../../../atoms/Icon'
import type { ProtectedBuildingInfo } from '@popety-io/types'
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalIcon,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  InfoRow,
  InfoLabel,
  InfoValue,
  InfoLink,
} from './ProtectedBuildingModal.styles'

export interface ProtectedBuildingModalProps {
  isOpen: boolean
  onClose: () => void
  protectedInfo: ProtectedBuildingInfo
}

const isUrl = (value: string): boolean => {
  return value.startsWith('http://') || value.startsWith('https://')
}

export const ProtectedBuildingModal: React.FC<ProtectedBuildingModalProps> = ({
  isOpen,
  onClose,
  protectedInfo,
}) => {
  const { t } = useTranslation('landAnalysis')
  const { t: tCommon } = useTranslation('common')

  if (!isOpen) return null

  const entries = Object.entries(protectedInfo).filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  )

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalIcon>
            <Icon name="shield" size="medium" />
          </ModalIcon>
          <ModalTitle>
            {t('buildingDetails.protectedModal.title', {
              defaultValue: 'Protected Building Information',
            })}
          </ModalTitle>
          <ModalCloseButton onClick={onClose}>
            <Icon name="close" size="medium" />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          {entries.map(([key, value]) => {
            const stringValue = String(value)
            const isLinkValue = isUrl(stringValue)

            return (
              <InfoRow key={key}>
                <InfoLabel>{key}</InfoLabel>
                {isLinkValue ? (
                  <InfoLink
                    href={stringValue}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('buildingDetails.protectedModal.clickHere', {
                      defaultValue: 'Click here',
                    })}
                    <Icon name="external-link" size="small" />
                  </InfoLink>
                ) : (
                  <InfoValue>{stringValue}</InfoValue>
                )}
              </InfoRow>
            )
          })}
        </ModalBody>

        <ModalFooter>
          <Button variant="secondary" onClick={onClose}>
            {tCommon('common.close', {
              defaultValue: 'Close',
            })}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  )
}
